import { useState, useCallback } from "react";
import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps } from "../utils/environment";
import { BookingDocument } from "../types/bookings";

interface UseDocumentUploadReturn {
  upload: (
    bookingId: string,
    file: File
  ) => Promise<BookingDocument | undefined>;
  progress: number;
  isUploading: boolean;
  error: string | null;
}

export const useDocumentUpload = (): UseDocumentUploadReturn => {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (
      bookingId: string,
      file: File
    ): Promise<BookingDocument | undefined> => {
      setIsUploading(true);
      setProgress(0);
      setError(null);

      try {
        const token = await dmnIdentity.getAccessToken();
        const environmentVars = getEnvironmentProps(token);

        return await new Promise<BookingDocument | undefined>((resolve) => {
          const xhr = new XMLHttpRequest();
          const formData = new FormData();
          formData.append("file", file);

          xhr.upload.onprogress = (event: ProgressEvent) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round(
                (event.loaded / event.total) * 100
              );
              setProgress(percentComplete);
            }
          };

          xhr.onload = () => {
            setIsUploading(false);
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const result = JSON.parse(xhr.responseText);
                // API may return { document: {...} }, { data: {...} }, or the document directly
                const document = result?.document ?? result?.data ?? result;
                resolve(document?.id ? document : undefined);
              } catch {
                resolve(undefined);
              }
            } else if (xhr.status === 409) {
              setError(
                "This booking has been updated by someone else. Please refresh and try again."
              );
              resolve(undefined);
            } else {
              setError("Failed to upload document. Please try again.");
              resolve(undefined);
            }
          };

          xhr.onerror = () => {
            setIsUploading(false);
            setError("Failed to upload document. Please try again.");
            resolve(undefined);
          };

          xhr.open(
            "POST",
            `${environmentVars?.COLLINS_API_BASE_URL}/bookings/${bookingId}/documents`
          );
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          xhr.send(formData);
        });
      } catch {
        setIsUploading(false);
        setError("Failed to upload document. Please try again.");
        return undefined;
      }
    },
    []
  );

  return { upload, progress, isUploading, error };
};
